/**
 * SKU performance - /sales/skus (SalesHubPage renders the tabs; this page renders its own header and control bar).
 *
 *   <SalesSkusPage />                        owns its scope: renders the h1 and SalesControlBar itself
 *   <SalesSkusPage scope={scope} setScope={setScope} />   parent owns the header and control bar
 *
 * Loads GET /api/sales-hub?dimension=sku for the page scope. Quick filters (All selling, Top sellers,
 * Fast growing, Declining, Low cover, Not selling) with counts, a search box, sortable columns,
 * pagination (TableControls), CSV of the visible columns, "New" badges, coloured days of cover.
 * A row (or card on phones) opens ProductDetailDrawer; a platform in the drawer filters the page.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Download, Search, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { LoadError } from '../components/LoadError';
import { SortHeader, TablePager, useTableControls } from '../components/TableControls';
import { useSalesData } from '../components/sales/salesApi';
import { DeltaBadge, formatDelta } from '../components/sales/DeltaBadge';
import { ProductDetailDrawer } from '../components/sales/ProductDetailDrawer';
import { compareWords, dayCount, downloadCsv, number, shortMoney, wholeMoney } from '../components/sales/format';
import {
  CLEAR_FILTERS, ChangePct, CoverBadge, EmptyState, TableSkeleton, ViewHeader, changePctText, hasFilters, pctText, usePageScope,
} from '../components/sales/views/common';

const hasActivity = row => Boolean(row.revenue || row.previous_revenue || row.units || row.returned_units);
const TOP_N = 20;

const QUICK_FILTERS = [
  { key: 'all', label: 'All selling', hint: 'SKUs with sales or returns in either period', test: hasActivity, sort: { key: 'revenue', dir: 'desc' } },
  { key: 'top', label: 'Top sellers', hint: `Top ${TOP_N} SKUs by revenue`, test: row => row.revenue > 0, limit: TOP_N, sort: { key: 'revenue', dir: 'desc' } },
  { key: 'growing', label: 'Fast growing', hint: 'Revenue up 20% or more, or new this period; ranked by ₹ gain', test: row => row.revenue_delta > 0 && (row.is_new || (row.revenue_change ?? 0) >= 20), sort: { key: 'revenue_delta', dir: 'desc' } },
  { key: 'declining', label: 'Declining', hint: 'Revenue down; ranked by ₹ lost', test: row => row.revenue_delta < 0, sort: { key: 'revenue_delta', dir: 'asc' } },
  { key: 'low_cover', label: 'Low cover', hint: 'Selling SKUs with under 15 days of stock', test: row => row.days_of_cover != null && row.days_of_cover < 15, sort: { key: 'days_of_cover', dir: 'asc' } },
  { key: 'not_selling', label: 'Not selling', hint: 'Has stock but sold nothing this period', test: row => (row.stock || 0) > 0 && !row.units, sort: { key: 'stock', dir: 'desc' } },
];

const EMPTY_TEXT = {
  top: 'No SKU sold in this period.',
  growing: 'No SKU grew by 20% or more. Try a longer period or compare with last year.',
  declining: 'Nothing is declining. Every SKU held or grew its revenue.',
  low_cover: 'No selling SKU is under 15 days of cover.',
  not_selling: 'Every SKU with stock sold at least one unit.',
};

const SEARCH_KEYS = ['key', 'product_name', 'brand', 'category'];
const SORT_VALUE = {
  name: row => (row.product_name || row.key || '').toLowerCase(),
  revenue_change: row => (row.is_new ? Number.POSITIVE_INFINITY : row.revenue_change),
};

const CSV_HEADER = ['SKU', 'Product', 'Brand', 'Category', 'Revenue (₹)', 'Previous revenue (₹)', 'Change (₹)', 'Change (%)',
  'Units', 'Previous units', 'Avg selling price (₹)', 'Returned units', 'Return rate (%)', 'Stock', 'Days of cover'];
const round = (value, digits = 0) => (value == null ? '' : Number(Number(value).toFixed(digits)));
const csvRow = row => [row.key, row.product_name, row.brand, row.category, round(row.revenue), round(row.previous_revenue),
  round(row.revenue_delta), changePctText(row) === '—' ? '' : changePctText(row).replace('−', '-'), row.units, row.previous_units,
  round(row.avg_selling_price), row.returned_units, round(row.return_rate, 1), row.stock ?? '', round(row.days_of_cover, 1)];

const NewBadge = () => (
  <span className="ml-1.5 inline-flex rounded-full bg-sky-50 px-1.5 text-[10px] font-semibold uppercase tracking-wide text-sky-800 ring-1 ring-inset ring-sky-200"
    title="No sales in the comparison period">New</span>
);

export default function SalesSkusPage(props) {
  const { scope, setScope, salesScope } = usePageScope(props);
  const { data, loading, error, refresh } = useSalesData('hub', scope, { dimension: 'sku' });
  const [quick, setQuick] = useState('all');
  const [openSku, setOpenSku] = useState(null);
  const vs = compareWords(scope.compare, dayCount(scope.date_from, scope.date_to));

  const allRows = useMemo(() => data?.rows || [], [data]);
  const counts = useMemo(() => Object.fromEntries(QUICK_FILTERS.map(def => {
    const n = allRows.filter(def.test).length;
    return [def.key, def.limit ? Math.min(n, def.limit) : n];
  })), [allRows]);
  const active = QUICK_FILTERS.find(def => def.key === quick) || QUICK_FILTERS[0];
  const rows = useMemo(() => {
    const list = allRows.filter(active.test);
    return active.limit ? [...list].sort((a, b) => b.revenue - a.revenue).slice(0, active.limit) : list;
  }, [allRows, active]);

  const table = useTableControls(rows, { searchKeys: SEARCH_KEYS, initialSort: QUICK_FILTERS[0].sort, pageSize: 50, sortValue: SORT_VALUE });
  const { setSort } = table;
  useEffect(() => { setSort(active.sort); }, [active, setSort]);

  const exportCsv = () => downloadCsv([CSV_HEADER, ...table.all.map(csvRow)],
    `sku-performance_${scope.date_from}_${scope.date_to}${quick !== 'all' ? `_${quick}` : ''}.csv`);

  const header = (
    <ViewHeader salesScope={salesScope} title="SKU performance" testId="sales-skus-heading"
      brands={data?.options?.brands} categories={data?.options?.categories} onRefresh={refresh} loading={loading} />
  );

  if (error && !data) {
    return (
      <div className="space-y-4" data-testid="sales-skus-page">
        {header}
        <LoadError what="SKU sales" error={error} onRetry={refresh} retrying={loading} testId="sales-skus-error" />
      </div>
    );
  }

  // A dead period still has a useful list: stocked SKUs that sold nothing ("Not selling").
  const noSales = data && !allRows.some(hasActivity) && quick !== 'not_selling';

  return (
    <div className="space-y-4" data-testid="sales-skus-page">
      {header}
      {error && <LoadError what="the latest SKU numbers" error={error} onRetry={refresh} retrying={loading} />}

      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Quick filters">
        {QUICK_FILTERS.map(def => (
          <button key={def.key} type="button" aria-pressed={quick === def.key} title={def.hint} onClick={() => setQuick(def.key)}
            disabled={!data}
            className={`inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50 ${quick === def.key ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'}`}
            data-testid={`sku-quick-${def.key}`}>
            {def.label}
            {data && <span className={`rounded-full px-1.5 text-xs tabular-nums ${quick === def.key ? 'bg-white/20' : 'bg-gray-100 text-gray-600'}`}>{number(counts[def.key])}</span>}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" aria-hidden="true" />
          <Input value={table.query} onChange={event => table.setQuery(event.target.value)} placeholder="Search SKU, product, brand"
            aria-label="Search SKUs" className="bg-white pl-8 pr-8" data-testid="sku-search" />
          {table.query && (
            <button type="button" aria-label="Clear search" onClick={() => table.setQuery('')}
              className="absolute right-1.5 top-1.5 rounded p-1 text-gray-500 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <p className="text-xs text-gray-500">{active.hint} · changes {vs}</p>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!table.total} data-testid="sku-export">
            <Download className="mr-1.5 h-4 w-4" aria-hidden="true" />CSV
          </Button>
        </div>
      </div>

      {!data ? <TableSkeleton label="Loading SKU sales" /> : noSales ? (
        <EmptyState title="No sales in this period" testId="sales-skus-empty"
          action={(hasFilters(scope) || counts.not_selling > 0) && <>
            {counts.not_selling > 0 && <Button size="sm" variant="outline" onClick={() => setQuick('not_selling')} data-testid="sku-see-not-selling">See {number(counts.not_selling)} stocked SKU{counts.not_selling === 1 ? '' : 's'} that did not sell</Button>}
            {hasFilters(scope) && <Button size="sm" variant="outline" onClick={() => setScope(CLEAR_FILTERS)}>Clear filters</Button>}
          </>}>
          {hasFilters(scope) ? 'Nothing matches these filters. Clear them or pick a longer period.' : 'Pick a longer period, or upload sales from the overview tab.'}
          {data.meta?.latest_sale_date ? ` Latest sale on file: ${data.meta.latest_sale_date}.` : ''}
        </EmptyState>
      ) : !table.total ? (
        <EmptyState title={table.query ? `No SKU matches “${table.query}”` : `No ${active.label.toLowerCase()} SKUs`} testId="sales-skus-filter-empty"
          action={<>
            {table.query && <Button size="sm" variant="outline" onClick={() => table.setQuery('')}>Clear search</Button>}
            {quick !== 'all' && <Button size="sm" variant="outline" onClick={() => setQuick('all')}>Show all selling SKUs</Button>}
          </>}>
          {table.query ? 'Check the spelling or search by SKU code.' : EMPTY_TEXT[quick]}
        </EmptyState>
      ) : (
        <div className={`overflow-hidden rounded-xl border border-gray-200 bg-white transition-opacity ${loading ? 'opacity-60' : ''}`} aria-busy={loading}>
          {/* Phones: one card per SKU */}
          <ul className="divide-y divide-gray-100 md:hidden" data-testid="sku-cards">
            {table.pageRows.map(row => (
              <li key={row.key}>
                <button type="button" onClick={() => setOpenSku(row.key)} aria-label={`Open sales details for ${row.product_name || row.key}`}
                  className="block w-full px-4 py-3 text-left hover:bg-gray-50 focus:outline-none focus-visible:bg-blue-50">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-gray-900">{row.product_name || row.key}{row.is_new && <NewBadge />}</p>
                      <p className="truncate font-mono text-xs text-gray-500">{row.key}</p>
                    </div>
                    <span className="shrink-0 text-sm font-semibold tabular-nums">{shortMoney(row.revenue)}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
                    <DeltaBadge current={row.revenue} previous={row.previous_revenue} size="xs" />
                    <span>{number(row.units)} units</span>
                    {row.avg_selling_price ? <span>ASP {wholeMoney(row.avg_selling_price)}</span> : null}
                    {row.returned_units ? <span>{number(row.returned_units)} returns</span> : null}
                    <span>Stock {row.stock == null ? '—' : number(row.stock)}</span>
                    <CoverBadge days={row.days_of_cover} stock={row.stock} />
                  </div>
                </button>
              </li>
            ))}
          </ul>

          {/* Tablet and up: full table */}
          <div className="relative hidden overflow-x-auto md:block">
            <table className="w-full min-w-[960px] text-sm" data-testid="sku-table">
              <caption className="sr-only">SKU sales for the selected period, changes {vs}. Select a product to see its details.</caption>
              <thead className="border-b border-gray-200 bg-gray-50 text-xs text-gray-600">
                <tr>
                  <SortHeader label="Product" sortKey="name" table={table} />
                  <SortHeader label="Revenue" sortKey="revenue" table={table} align="right" />
                  <SortHeader label="₹ change" sortKey="revenue_delta" table={table} align="right" />
                  <SortHeader label="% change" sortKey="revenue_change" table={table} align="right" />
                  <SortHeader label="Units" sortKey="units" table={table} align="right" />
                  <SortHeader label="Avg price" sortKey="avg_selling_price" table={table} align="right" />
                  <SortHeader label="Returns" sortKey="return_rate" table={table} align="right" />
                  <SortHeader label="Stock" sortKey="stock" table={table} align="right" />
                  <SortHeader label="Days of cover" sortKey="days_of_cover" table={table} align="right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {table.pageRows.map(row => {
                  const delta = row.revenue_delta ?? (row.revenue - row.previous_revenue);
                  return (
                    <tr key={row.key} onClick={() => setOpenSku(row.key)} className="cursor-pointer hover:bg-blue-50/40" data-testid={`sku-row-${row.key}`}>
                      <td className="max-w-[320px] px-3 py-2">
                        <button type="button" onClick={event => { event.stopPropagation(); setOpenSku(row.key); }}
                          aria-label={`Open sales details for ${row.product_name || row.key}`}
                          className="block max-w-full rounded text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                          <span className="flex items-center"><span className="truncate font-medium text-gray-900">{row.product_name || row.key}</span>{row.is_new && <NewBadge />}</span>
                          <span className="block truncate text-xs text-gray-500"><span className="font-mono">{row.key}</span>{row.brand ? ` · ${row.brand}` : ''}{row.category && row.category !== 'Unknown' ? ` · ${row.category}` : ''}</span>
                        </button>
                      </td>
                      <td className="px-3 py-2 text-right font-medium tabular-nums">{shortMoney(row.revenue)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${delta > 0 ? 'text-green-700' : delta < 0 ? 'text-red-700' : 'text-gray-500'}`}>{delta ? formatDelta(delta) : '—'}</td>
                      <td className="px-3 py-2 text-right"><ChangePct row={row} /></td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {number(row.units)}
                        {row.previous_units != null && row.units !== row.previous_units && (
                          <span className="block text-[11px] text-gray-500">was {number(row.previous_units)}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{row.avg_selling_price ? wholeMoney(row.avg_selling_price) : '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {row.returned_units ? <>{number(row.returned_units)}<span className={`block text-[11px] ${row.return_rate >= 10 ? 'text-red-700' : 'text-gray-500'}`}>{pctText(row.return_rate)}</span></> : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{row.stock == null ? <span className="text-gray-400" title="No stock recorded">—</span> : number(row.stock)}</td>
                      <td className="px-3 py-2 text-right"><CoverBadge days={row.days_of_cover} stock={row.stock} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <TablePager table={table} itemLabel="SKUs" className="border-t border-gray-100" />
        </div>
      )}

      <ProductDetailDrawer sku={openSku} scope={scope} onClose={() => setOpenSku(null)} onPlatform={code => setScope({ platform: code })} />
    </div>
  );
}
